import { useState, useEffect, useCallback, useRef } from 'react';
import { apiUtils } from './useApi';
import { API_ENDPOINTS } from '../services/apiEndpoints';
export type QuestionnaireType = 'NOT_SELECTED' | 'FULL' | 'FASTTRACK';
// Hotel type definition
export interface DetailedScore {
  e: {
    biodiversity: number;
    climate: number;
    energy: number;
    waste: number;
    'Water Stewardship': number;
  };
  s: {
    'Community & Cultural Stewardship': number;
    'Human Rights, Justice & Wellbeing': number;
    'Responsible Sourcing': number;
    accessibility: number;
  };
  g: {
    auditability: number;
    'Training & Engagement': number;
    'Strategy & Management': number;
  };
}

export type UpgradePlan = 'Boost' | 'Essentials' | 'Basic';

export interface ProcessOverview {
  'Certifications and Standards': {
    e: { name: string; info_text: string }[];
    g: { name: string; info_text: string }[];
    s: { name: string; info_text: string }[];
  } | null;
  Strengths: {
    e: string[];
    s: string[];
    g: string[];
  };
  Progressing: {
    e: string[];
    s: string[];
    g: string[];
  };
  'Limited Focus': {
    e: string[];
    s: string[];
    g: string[];
  };
}

export interface HotelsResponse {
  data: Hotel[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: {
    [key: string]: string[];
  };
  metadata: Record<string, string>;
  hasAssignedHotelOrg: boolean;
  message: string;
  success: boolean;
}
export interface Hotel {
  id: string;
  giata_id: string;
  sabre_id: string;
  brand: string;
  chain: string;
  alo_hotel_status: string;
  hotel_image: string | null;
  alo_submission_status: string;
  fasttrack_submission_status: string | null;
  alo_resubmission_status: string | null;
  alo_resubmission_status_id: string | null;
  alo_hotel_status_free_trial_type: string;
  hotel_location_name: string;
  hotel_location_city: string;
  hotel_location_state: string;
  hotel_location_country: string;
  hotel_location_country_code: string;
  questionnaire_type: QuestionnaireType;
  score_alo: number;
  score_e: number;
  score_s: number;
  score_g: number;
  scores_detailed: DetailedScore;
  e__bio?: number;
  e__cli?: number;
  e__ene?: number;
  e__was?: number;
  e__wat?: number;
  s__com?: number;
  s__hum?: number;
  s__res?: number;
  s__acc?: number;
  g__aud?: number;
  g__tra?: number;
  g__str?: number;
  top_categories?: { key: string; score: number }[] | null;
  created_date: string;
  last_modified_date: string;
  resubmission_status_name: string | null;
  resubmission_status_description: string | null;
  AutoPublishDate: string | null;
  fundamental_score?: {
    Biodiversity: number;
    Climate: number;
    Energy: number;
    Waste: number;
    'Water Stewardship': number;
    'Community + Cultural Stewardship': number;
    'Human Rights, Justice + Wellbeing': number;
    'Responsible Sourcing': number;
    Accessibility: number;
    Auditability: number;
    'Training + Engagement': number;
    'Strategy + Management': number;
  };
  userPermissions: {
    canSaveQuestionnaire: boolean;
    canSubmitQuestionnaire: boolean;
    canResubmitQuestionnaire: boolean;
    canApproveScore: boolean;
    canPublishScore: boolean;
    canShowGBTA: boolean;
    canSaveReview: boolean;
    canCalculateAloScore: boolean;
    canReleaseToHotel: boolean;
    canEditTravelData: boolean;
    canEditHotelDetails: boolean;
    canApproveSource: boolean;
    canViewBenchmark: boolean;
    canReopenSubmission: boolean;
    canSwitchQuestionnaireType  : boolean;
  };
  performance_overview: ProcessOverview;
}

export interface UseHotelsParams {
  search?: string;
  page?: number;
  limit?: number;
  primarySortKey?: string;
  primarySortOrder?: 'asc' | 'desc' | null;
  secondarySortKey?: string;
  secondarySortOrder?: 'asc' | 'desc' | null;
  filters?: Record<string, string>;
  onlyFasttrack?: boolean;
}

export type LoadingState =
  | 'idle'
  | 'initial'
  | 'search'
  | 'filter'
  | 'sort'
  | 'pagination';
export type MetadataType = {
  [key: string]: string;
};

export interface UseHotelsReturn {
  data: Hotel[] | null;
  loadingState: LoadingState;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  filterMetadata: MetadataType;
  hasAssignedToHotelOrg: boolean;
  refetch: () => void;
  setLoadingState: React.Dispatch<React.SetStateAction<LoadingState>>;
}

export const useHotels = (params: UseHotelsParams = {}): UseHotelsReturn => {
  const [data, setData] = useState<Hotel[] | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [filterMetadata, setFilterMetadata] = useState<MetadataType>({
    all_count: '0',
    action_required_count: '0',
    under_review_count: '0',
    completed_count: '0',
    fastrack_count: '0',
  });
  const [hasAssignedToHotelOrg, setHasAssignedToHotelOrg] = useState(true);

  // Tracks if we've ever successfully fetched once
  const hasDataRef = useRef(false);

  const {
    search = '',
    page = 1,
    limit = 10,
    primarySortKey = '',
    primarySortOrder = 'desc',
    secondarySortKey = 'hotel_location_name',
    secondarySortOrder = 'asc',
    filters = {},
  } = params;

  const fetchHotels = useCallback(async () => {
    if (!hasDataRef.current) {
      setLoadingState('initial');
    }

    try {
      const queryParams: Record<string, any> = {};

      if (search) queryParams.search = search;

      // Add primary sort if provided
      if (primarySortKey && primarySortOrder) {
        queryParams.primarySortKey = primarySortKey;
        queryParams.primarySortOrder = primarySortOrder;
      }

      // Add secondary sort if provided
      if (secondarySortKey && secondarySortOrder) {
        queryParams.secondarySortKey = secondarySortKey;
        queryParams.secondarySortOrder = secondarySortOrder;
      }

      queryParams.page = page;
      queryParams.limit = limit;

      if (Object.keys(filters).length > 0) {
        Object.keys(filters).forEach(key => {
          if (filters[key]) {
            queryParams[key] = filters[key];
          }
        });
      }

      const response = await apiUtils.get<HotelsResponse>(
        API_ENDPOINTS.hotels.list,
        queryParams
      );

      console.log('Hotels Data Response:', response);

      const hotelData = response.data.data;
      const paginationData = response.data.pagination;
      const metadata = response.data.metadata;
      const hasAssignedToHotelOrgData = response.data.hasAssignedHotelOrg;

      console.log('Has Assigned To Hotel Org:', hasAssignedToHotelOrgData);
      setHasAssignedToHotelOrg(hasAssignedToHotelOrgData);
      // Brief loading state before updating data for smooth transition
      if (search) {
        setLoadingState('filter');
      }
      // setData(null);

      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 50));

      setFilterMetadata(metadata);
      setData(hotelData);
      setCurrentPage(paginationData.currentPage);
      setTotalCount(paginationData.totalCount);
      setTotalPages(paginationData.totalPages);
      setHasPrevPage(paginationData.hasPrevPage);
      setHasNextPage(paginationData.hasNextPage);
      // setLoadingState('idle');
      hasDataRef.current = true;
    } catch (err) {
      console.error('❌ API call failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch hotels');
      setData([]);
      setTotalCount(0);
      setHasNextPage(false);
      hasDataRef.current = true; // Even on error, mark as attempted
      // setLoadingState('idle');
    } finally {
      setLoadingState('idle');
    }
  }, [
    search,
    page,
    limit,
    primarySortKey,
    primarySortOrder,
    secondarySortKey,
    secondarySortOrder,
    filters,
  ]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  return {
    data,
    loadingState,
    setLoadingState,
    filterMetadata,
    totalPages,
    error,
    totalCount,
    currentPage,
    hasNextPage,
    hasPrevPage,
    hasAssignedToHotelOrg,
    refetch: fetchHotels,
  };
};
